import { lazyWithRetry as lazy } from '@utils/lazyWithRetry';
import { RouteConfig } from '@routes/types';
import {
  rideshareMonitoringRoute,
  rideshareProfileRoute,
  rideshareReportingRoute,
  rideshareUtilityRoutes,
} from './shared/rideshareCommonRoutes';

const ContractorsCompliance = lazy(
  () => import('@component/ContractorsCompliance/ContractorsCompliance')
);
const ContractorsInfo = lazy(
  () => import('@component/ContractorsInfo/ContractorsInfo')
);
const ContractorsOperations = lazy(
  () => import('@component/ContractorsOperations/ContractorsOperations')
);
const ContractorsPersonnel = lazy(
  () => import('@component/ContractorsPersonnel/ContractorsPersonnel')
);
const ContractorsVehicles = lazy(
  () => import('@component/ContractorsVehicles/ContractorsVehicles')
);
const Locations = lazy(() => import('@component/Locations/Locations'));
const MembersInfo = lazy(
  () => import('@component/Member/MembersInfo/MembersInfo')
);
const MyRoomAccess = lazy(
  () => import('@component/Payer/MyRoomAccess/MyRoomAccess')
);
const Agreements = lazy(() => import('@component/Agreements/Agreements'));
const MyRoomAuditLogs = lazy(
  () => import('@component/AuditLog/MyRoomAuditLogs/MyRoomAuditLogs')
);
const MyRoomBenefits = lazy(
  () => import('@component/Member/MyRoomBenefits/MyRoomBenefits')
);
const AuthContacts = lazy(() => import('@component/AuthContacts/AuthContacts'));
const MyRoomDocs = lazy(
  () => import('@component/Documents/MyRoomDocs/MyRoomDocs')
);
const MyRoomExclusions = lazy(
  () => import('@component/Payer/MyRoomExcludedContractors/MyRoomExclusions')
);
const PayerInfo = lazy(() => import('@component/Payer/PayerInfo/PayerInfo'));
const MyRoomPreferences = lazy(
  () => import('@component/Member/MyRoomPreferences/MyRoomPreferences')
);
const RidesPage = lazy(() => import('@component/RidesPage/RidesPage'));
const Services = lazy(() => import('@component/Services/Services'));
const Permissions = lazy(() => import('@component/Permissions/Permissions'));
const PlacesInfo = lazy(() => import('@component/PlacesInfo/PlacesInfo'));
const PlacesService = lazy(
  () => import('@component/PlacesService/PlacesService')
);
const QualityIncidents = lazy(
  () => import('@component/QualityIncidents/QualityIncidents')
);
const RidesDetails = lazy(() => import('@component/Ride/RidesDetails'));
const StandingOrder = lazy(
  () => import('@component/Ride/StandingOrder/StandingOrder')
);
const SurveyTemplate = lazy(
  () => import('@component/SurveyTemplate/SurveyTemplate')
);
const Users = lazy(() => import('@component/Users/Users'));
const HomeWrapper = lazy(() => import('@page/HomeWrapper'));
const PageWrapper = lazy(() => import('@page/PageWrapper'));
const Assignment = lazy(() => import('@component/Assignment/Assignment'));
const AuthContactsLightNew = lazy(
  () =>
    import('@component/LightVersion/AuthContactsLightNew/AuthContactsLightNew')
);
const PageNotFound = lazy(() => import('@component/PageNotFound/PageNotFound'));
const CreateContractorFlow = lazy(
  () => import('@component/CreateContractorFlow/CreateContractorFlow')
);

const PayerSettings = lazy(
  () => import('@component/LightVersion/Payer/PayerSettings/PayerSettings')
);

const withIdVariants = (children: RouteConfig[]): RouteConfig[] =>
  children.flatMap((route) =>
    route.path === '*' || route.path.includes(':id')
      ? [route]
      : [route, { ...route, path: `${route.path}/:id` }]
  );

export const rideshareFullRoutes: RouteConfig[] = [
  {
    path: '/',
    element: HomeWrapper,
    children: [
      {
        path: 'payors',
        element: PageWrapper,
        children: withIdVariants([
          { path: 'information', element: PayerInfo },
          { path: 'contacts', element: AuthContacts },
          { path: 'agreements', element: Agreements },
          { path: 'services', element: Services },
          { path: 'documents', element: MyRoomDocs },
          { path: 'access', element: MyRoomAccess },
          { path: 'settings', element: PayerSettings },
          { path: 'audit-logs', element: MyRoomAuditLogs },
          { path: 'exclusions', element: MyRoomExclusions },
          { path: '*', element: PageNotFound },
        ]),
      },
      {
        path: 'members',
        element: PageWrapper,
        children: withIdVariants([
          { path: 'information', element: MembersInfo },
          { path: 'benefits', element: MyRoomBenefits },
          { path: 'preferences', element: MyRoomPreferences },
          { path: 'documents', element: MyRoomDocs },
          { path: 'contacts', element: AuthContacts },
          { path: 'rides', element: RidesPage },
          { path: 'audit-logs', element: MyRoomAuditLogs },
          { path: '*', element: PageNotFound },
        ]),
      },
      {
        path: 'administrator',
        element: PageWrapper,
        children: withIdVariants([
          { path: 'users', element: Users },
          { path: 'permissions', element: Permissions },
          { path: 'survey-templates', element: SurveyTemplate },
          { path: '*', element: PageNotFound },
        ]),
      },
      {
        path: 'places',
        element: PageWrapper,
        children: [
          { path: 'information', element: PlacesInfo },
          { path: 'contacts', element: AuthContacts },
          { path: 'locations', element: Locations },
          { path: 'services', element: PlacesService },
          { path: 'documents', element: MyRoomDocs },
          { path: 'docs', element: MyRoomDocs },
          { path: 'access', element: MyRoomAccess },
          { path: 'rides', element: RidesPage },
          { path: 'audit-logs', element: MyRoomAuditLogs },
          { path: '*', element: PageNotFound },
        ],
      },
      {
        path: 'rides',
        element: PageWrapper,
        children: [
          { path: 'information', element: RidesDetails },
          { path: 'information/:id', element: RidesDetails },
          { path: 'standing-order', element: StandingOrder },
          { path: 'standing-order/:id', element: StandingOrder },
          { path: 'contacts', element: AuthContacts },
          { path: 'quality-incidents', element: QualityIncidents },
          { path: 'access', element: MyRoomAccess },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
          { path: '*', element: PageNotFound },
        ],
      },
      {
        path: 'contractors',
        element: PageWrapper,
        children: withIdVariants([
          { path: 'create', element: CreateContractorFlow },
          { path: 'information', element: ContractorsInfo },
          { path: 'contacts', element: AuthContacts },
          { path: 'agreements', element: Agreements },
          { path: 'services', element: Services },
          { path: 'operations', element: ContractorsOperations },
          { path: 'compliance', element: ContractorsCompliance },
          { path: 'vehicles', element: ContractorsVehicles },
          { path: 'personnel', element: ContractorsPersonnel },
          { path: 'documents', element: MyRoomDocs },
          { path: 'access', element: MyRoomAccess },
          { path: 'rides', element: RidesPage },
          { path: 'audit-logs', element: MyRoomAuditLogs },
          { path: '*', element: PageNotFound },
        ]),
      },
      {
        path: 'assignment',
        element: PageWrapper,
        children: [
          { path: 'information', element: Assignment },
          { path: '*', element: PageNotFound },
        ],
      },
      {
        path: 'standing-order',
        element: PageWrapper,
        children: [
          { path: 'details', element: StandingOrder },
          { path: 'details/:id', element: StandingOrder },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
          { path: '*', element: PageNotFound },
        ],
      },
      rideshareMonitoringRoute,
      rideshareProfileRoute,
      rideshareReportingRoute,
    ],
  },
  ...rideshareUtilityRoutes,
];
